import { useState, useEffect } from 'react'

const useVoicesFilter = (voicesObject) => {

    const [vendors, setVendors] = useState([])
    const [selectedVendor, setSelectedVendor] = useState('')
    const [languages, setLanguages] = useState([])
    const [genders, setGenders] = useState([])
    const [voices, setVoices] = useState([])
    const [selectedLanguage, setSelectedLanguage] = useState('')
    const [selectedGender, setSelectedGender] = useState('')
    const [voicesForLanguage, setVoicesForLanguage] = useState([])

    // Extract vendors from the voicesObject
    useEffect(() => {
        if (voicesObject && typeof voicesObject === 'object') {
            const vendorOptions = Object.keys(voicesObject).map(vendor => {
                // Extract the part after '-' if it exists, otherwise use the full vendor name
                const displayName = vendor.includes('-') ? vendor.split('-')[1] : vendor;
                return {
                    value: vendor,
                    label: displayName.charAt(0).toUpperCase() + displayName.slice(1) // Capitalize first letter
                };
            });
            setVendors(vendorOptions);

            // Set "Istnetworks" as default if available
            const istnetworksVendor = vendorOptions.find(vendor =>
                vendor.value.toLowerCase() === 'istnetworks');
            if (istnetworksVendor) {
                setSelectedVendor(istnetworksVendor);
            }
        }
    }, [voicesObject]);

    // Extract languages for selected vendor
    useEffect(() => {
        if (selectedVendor && voicesObject) {
            const voicesForVendor = voicesObject[selectedVendor.value] || [];
            const languages = voicesForVendor.map((voice) => voice.language);
            const uniqueLanguages = [...new Set(languages)].map((language) => {
                return {
                    value: language,
                    label: language
                }
            });
            setLanguages(uniqueLanguages);
            // Reset downstream selections
            setSelectedLanguage('');
            setSelectedGender('');
            setVoicesForLanguage([]);
        } else {
            setLanguages([]);
        }
    }, [selectedVendor, voicesObject]);

    // Set genders for selected language
    useEffect(() => {
        if (selectedVendor && selectedLanguage) {
            const voicesForVendor = voicesObject[selectedVendor.value] || [];
            const voicesForLanguage = voicesForVendor.filter((voice) => voice.language === selectedLanguage.value);

            // Get unique genders from the filtered voices
            const gendersInLanguage = voicesForLanguage.map(voice => voice.gender?.toLowerCase());
            const uniqueGenders = [...new Set(gendersInLanguage)].filter(Boolean);

            // Create gender options
            const genderOptions = uniqueGenders.map(gender => ({
                value: gender,
                label: gender
            }));

            setGenders(genderOptions);

            // Reset voice selection
            setSelectedGender('');
            setVoicesForLanguage([]);
        } else {
            setGenders([]);
        }
    }, [selectedLanguage, selectedVendor, voicesObject]);

    // Filter voices by gender
    useEffect(() => {
        if (selectedVendor && selectedLanguage && selectedGender) {
            const voicesForVendor = voicesObject[selectedVendor.value] || [];
            const voicesForLanguage = voicesForVendor.filter((voice) => voice.language === selectedLanguage.value);
            const voicesForGender = voicesForLanguage.filter(voice => voice.gender?.toLowerCase() === selectedGender.value);

            setVoicesForLanguage(
                voicesForGender.map((voice) => {
                    return {
                        value: voice.name_value ? voice.name_value : voice.name,
                        label: voice.display_name ? voice.display_name : voice.name,
                        vendor: voice.vendor || selectedVendor.value,
                        voiceGender: voice.gender,
                        voiceLanguage: voice.language
                    }
                })
            );
        } else {
            setVoicesForLanguage([]);
        }
    }, [selectedGender, selectedLanguage, selectedVendor, voicesObject]);

    return [
        vendors,
        selectedVendor,
        setSelectedVendor,
        languages,
        selectedLanguage,
        setSelectedLanguage,
        genders,
        selectedGender,
        setSelectedGender,
        voicesForLanguage
    ]
}

export { useVoicesFilter }